<template>
  <transition name="modal">
    <div 
      class="modal-mask" 
      @click.self="$emit('close')"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      @keydown.esc="$emit('close')"
    >
      <div class="modal-wrapper">
        <div class="modal-container" ref="modalContainer">
          <button 
            class="modal-default-button" 
            @click="$emit('close')"
            :aria-label="locale && locale.lukk_modal && locale.lukk_modal[lang] || 'Lukk'"
            ref="closeButton"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
          <div class="modal-body desc"></div>
          <h1 id="modal-title">{{locale.modal_overskrift[lang]}}</h1>
          <div class="modal-header">{{locale.modal_overskrift_tekst[lang]}}</div>

          <div class="modal-body">
            <h2 class="semibold">{{locale.modal_geigerteller_overskrift[lang]}}</h2>
            <div>{{locale.modal_geigerteller_tekst[lang]}}</div>
          </div>
          <div class="modal-body">
            <h2 class="semibold">{{locale.modal_bakgrunnsstraaling_overskrift[lang]}}</h2>
            <div>{{locale.modal_bakgrunnsstraaling_tekst[lang]}}</div>
          </div>
          <div class="modal-body">
            <h2 class="semibold">{{locale.modal_radioaktivkilde_overskrift[lang]}}</h2>
            <div>{{locale.modal_radioaktivkilde_tekst[lang]}}</div>
          </div>
          <div class="modal-body">
            <h2 class="semibold">{{locale.modal_avstand_overskrift[lang]}}</h2>
            <div>{{locale.modal_avstand_tekst[lang]}}</div>
          </div>
          <div class="modal-body">
            <h2 class="semibold">{{locale.modal_materiale_overskrift[lang]}}</h2>
            <div v-html="locale.modal_materiale_tekst[lang]"></div>
          </div>
          
          <!-- Keyboard shortcuts seksjson -->
          <div class="modal-body keyboard-shortcuts" v-if="locale.keyboard_shortcuts_title">
            <h2 class="semibold">{{locale.keyboard_shortcuts_title[lang]}}</h2>
            <dl class="shortcuts-list">
              <dt>Tab</dt>
              <dd>{{locale.keyboard_tab_desc && locale.keyboard_tab_desc[lang]}}</dd>
              
              <dt>Shift + Tab</dt>
              <dd>{{locale.keyboard_shift_tab_desc && locale.keyboard_shift_tab_desc[lang]}}</dd>
              
              <dt>Enter / Space</dt>
              <dd>{{locale.keyboard_activate_desc && locale.keyboard_activate_desc[lang]}}</dd>
              
              <dt>↑ ↓ ← →</dt>
              <dd>{{locale.keyboard_arrows_desc && locale.keyboard_arrows_desc[lang]}}</dd>
              
              <dt>Home</dt>
              <dd>{{locale.keyboard_home_desc && locale.keyboard_home_desc[lang]}}</dd>
              
              <dt>End</dt>
              <dd>{{locale.keyboard_end_desc && locale.keyboard_end_desc[lang]}}</dd>
              
              <dt>Escape</dt>
              <dd>{{locale.keyboard_escape_desc && locale.keyboard_escape_desc[lang]}}</dd>
            </dl>
          </div>
          
          <div class="modal-footer">
            <slot name="footer"></slot>
            <button 
              class="modal-close-bottom"
              @click="$emit('close')"
              :aria-label="locale && locale.lukk_modal && locale.lukk_modal[lang] || 'Lukk'"
              ref="lastFocusable"
            >
              {{ locale && locale.lukk_modal && locale.lukk_modal[lang] || 'Lukk' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  props: ["mode", "lang", "locale"],
  methods: {
    listenForKeyUp(event) {
      let keyCode = event.which || event.keyCode;
      if (keyCode == 27) {
        this.$emit("close");
      }
    },
    trapFocus(event) {
      const modal = this.$refs.modalContainer;
      if (!modal) return;
      
      const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const focusableElements = modal.querySelectorAll(focusableSelectors);
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          // Shift+Tab: gå bakover
          if (document.activeElement === firstFocusable) {
            event.preventDefault();
            lastFocusable.focus();
          }
        } else {
          // Tab: gå fremover
          if (document.activeElement === lastFocusable) {
            event.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    }
  },
  mounted() {
    this.$nextTick(function() {
      window.addEventListener("keyup", this.listenForKeyUp);
      // Fokus på lukk-knapp når modal åpnes
      if (this.$refs.closeButton) {
        this.$refs.closeButton.focus();
      }
      // Legg til focus trap
      const modal = this.$refs.modalContainer;
      if (modal) {
        modal.addEventListener('keydown', this.trapFocus);
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener("keyup", this.listenForKeyUp);
    const modal = this.$refs.modalContainer;
    if (modal) {
      modal.removeEventListener('keydown', this.trapFocus);
    }
  }
};
</script>

<style scoped>
/* MODAL STUFF */
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 1);
  color: black;
  display: table;
  transition: opacity 0.3s ease;
}
@media (max-width: 899px) {
  .modal-mask {
    font-size: 0.55rem;
  }
}
.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.modal-container {
  position: relative;
  width: calc(100vw - 60px);
  margin: 0px auto;
  padding: 20px 30px;
  border-radius: 2px;
  transition: all 0.3s ease;
  overflow: auto;
  max-height: 100vh;
}
@media only screen and (min-width: 766px) {
  .modal-container {
    width: 80%;
  }
}
.modal-container > .modal-default-button {
  position: absolute;
  top: 5px;
  right: 5px;
}
.modal-header {
  /* styling */
}

.modal-body {
  margin: 20px 0;
}

.modal-body.body {
  white-space: pre;
}

.modal-default-button {
  float: right;
}

.modal-close-bottom {
  display: block;
  margin: 2rem auto 2rem;
  padding: 0.75rem 3rem;
  background: #333;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

.modal-close-bottom:hover {
  background: #000;
}

.modal-close-bottom:focus-visible {
  outline: 3px solid #000;
  outline-offset: 3px;
}

/* Keyboard shortcuts */
.keyboard-shortcuts {
  background: #f5f5f5;
  padding: 1.5rem;
  border-radius: 8px;
}

.shortcuts-list {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5rem 1.5rem;
  margin: 0.5rem 0 0 0;
}

.shortcuts-list dt {
  font-weight: bold;
  font-family: monospace;
  background: #333;
  color: #fff;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  text-align: center;
  align-self: start;
}

.shortcuts-list dd {
  margin: 0;
  padding: 0.25rem 0;
  align-self: start;
}

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

/* END OF MODAL STUFF */
</style>
